import { DeleteFilled, LinkOutlined, SearchOutlined } from "@ant-design/icons";
import type { ActionType, ProColumns } from "@ant-design/pro-components";
import { ProTable } from "@ant-design/pro-components";
import {
  Button,
  Input,
  Space,
  InputRef,
  DatePicker,
  Dropdown,
  message,
  Row,
  Col,
  Popconfirm,
} from "antd";
import { useRef, useState } from "react";
import Export from "src/common/components/Export";
import lang from "src/lang";

import { DBInvoicePayment } from "@modules/sales/types/types";
import coreFunctions from "src/common/utils/coreFunctions";
import apiRequest from "src/common/utils/apiRequest";
import { useNavigate } from "react-router-dom";

const { RangePicker } = DatePicker;

const PaymentsTable = () => {
  const actionRef = useRef<ActionType>();
  const [pageSize, setPageSize] = useState<number>(10);
  const searchInput = useRef<InputRef>(null);
  const [allSearch, setAllSearch] = useState<string>("");
  const [tableData, setTableData] = useState<DBInvoicePayment[]>([]);
  const [searchAttributes, setSearchAttributes] = useState<
    { key: string; value: string }[]
  >([]);
  const [filters, setFilters] = useState<
    { key: string; value: any; type: string }[]
  >([]);

  const expobj = new Export("payments, " + new Date().toDateString());
  const navigate = useNavigate();

  const searchCols = (field: string, label: string) => {
    return {
      filterDropdown: (props: any) => {
        const { close } = props;

        setTimeout(() => {
          if (searchInput.current) {
            searchInput.current.focus();
          }
        }, 100);

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={searchInput}
              placeholder={lang.get("payment", [`Search ${label}`])}
              style={{ marginBottom: 8, display: "block" }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  close();
                  if (actionRef.current) {
                    actionRef.current.reload();
                  }
                }
              }}
            />
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="small"
                onClick={() => {
                  if (actionRef.current) actionRef.current.reload();
                }}
                style={{ width: 90 }}
              >
                {lang.get("payment", ["Search"])}
              </Button>
              <Button
                onClick={() => close()}
                size="small"
                style={{ width: 90 }}
              >
                {lang.get("payment", ["Close"])}
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    };
  };

  const handleDelete = (id: string) => {
    const API_REQUEST = new apiRequest("sales/payments/invoice/delete", true);
    API_REQUEST.send(
      {
        id,
      },
      (response: { status: string; message: string; hr_record: string }) => {
        if (response && response.status == "success") {
          actionRef.current?.reload();
          message.success(response.message);
        } else {
          message.error(response.message);
        }
      }
    );
  };

  const columns: ProColumns[] = [
    {
      title: "#",
      dataIndex: "#",
      sorter: true,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Invoice",
      dataIndex: "invoice",
      sorter: true,
      ...searchCols("invoice", "Invoice #"),
      render: (text, record) => (
        <Button
          type="link"
          onClick={() =>
            navigate("/modules/sales/invoices/" + record.invoice.id)
          }
        >
          {record.invoice.number} <LinkOutlined />
        </Button>
      ),
    },
    {
      title: "Payment Mode",
      dataIndex: "method",
      sorter: true,
      ...searchCols("method", "Payment Mode"),
    },
    {
      title: "Transaction ID",
      dataIndex: "reference",
      sorter: true,
      ...searchCols("reference", "Transaction ID"),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      sorter: true,
      ...searchCols("customer", "Customer"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      sorter: true,
      ...searchCols("amount", "Amount"),
    },
    {
      title: "Date",
      dataIndex: "date",
      sorter: true,
      ...searchCols("date", "Date"),
    },
    {
      title: "Action",
      dataIndex: "date",
      sorter: false,
      render: (_, record) => (
        <Row>
          <Col>
            <Popconfirm
              title={lang.get("hr", [
                "Are you sure you want to delete this payment?",
              ])}
              okText="Yes"
              onConfirm={(e) => {
                e?.currentTarget?.setAttribute("disabled", "true");
                handleDelete(record.id);
              }}
            >
              <Button size="small" danger>
                <DeleteFilled />
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      ),
    },
  ];

  const exportItems = [
    {
      key: "csv",
      label: (
        <a
          onClick={() => {
            const csvData = tableData.map((r, index) => ({
              [lang.get("payment", ["Payment #"])]: index + 1,
              [lang.get("payment", ["Invoice #"])]: r.invoice.number,
              [lang.get("payment", ["Payment Mode"])]: r.method,
              [lang.get("payment", ["Transaction ID"])]: r.reference,
              [lang.get("payment", ["Customer"])]: r.customer,
              [lang.get("payment", ["Amount"])]: r.amount,
              [lang.get("payment", ["Date"])]: r.date,
            }));
            expobj.exportToCSV(csvData);
          }}
        >
          {lang.get("payment", ["Export CSV"])}
        </a>
      ),
    },
    {
      key: "excel",
      label: (
        <a
          onClick={() => {
            const excelData = tableData.map((r, index) => ({
              [lang.get("payment", ["Payment #"])]: index + 1,
              [lang.get("payment", ["Invoice #"])]: r.invoice.number,
              [lang.get("payment", ["Payment Mode"])]: r.method,
              [lang.get("payment", ["Transaction ID"])]: r.reference,
              [lang.get("payment", ["Customer"])]: r.customer,
              [lang.get("payment", ["Amount"])]: r.amount,
              [lang.get("payment", ["Date"])]: r.date,
            }));
            expobj.exportToExcel(excelData);
          }}
        >
          {lang.get("payment", ["Export Excel"])}
        </a>
      ),
    },
    {
      key: "pdf",
      label: (
        <a
          onClick={() => {
            expobj.exportToPDF({
              head: [
                [
                  lang.get("payment", ["Payment #"]),
                  lang.get("payment", ["Invoice #"]),
                  lang.get("payment", ["Payment Mode"]),
                  lang.get("payment", ["Transaction ID"]),
                  lang.get("payment", ["Customer"]),
                  lang.get("payment", ["Amount"]),
                  lang.get("payment", ["Date"]),
                ],
              ],
              body: tableData.map((r, index) => [
                index + 1,
                r.invoice.number,
                r.method,
                r.reference,
                r.customer,
                r.amount,
                r.date,
              ]),
            });
          }}
        >
          {lang.get("payment", ["Export PDF"])}
        </a>
      ),
    },
  ];

  return (
    <ProTable
      headerTitle={lang.get("payment", ["Payments"])}
      columns={columns}
      actionRef={actionRef}
      dataSource={tableData}
      request={async (params, sort, filter) => {
        await new coreFunctions().waitRequest(10);

        const processedFilters = Object.entries(filter || {}).reduce(
          (acc, [key, value]) => {
            if (value && value.length > 0) {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, (string | number)[]>
        );

        const payload = {
          params: params,
          sorts: sort,
          other_filters: filters,
          filters: processedFilters,
          searches: searchAttributes,
          all_search: allSearch,
        };

        const API_REQUEST = new apiRequest("sales/payments/invoice/get", true);
        const response = await API_REQUEST.send(payload);

        if (response && response.status === "success") {
          const fetchedData = response.data || [];
          setTableData(fetchedData);
          const total = response.total || 0;

          return {
            data: fetchedData,
            total: total,
            success: true,
          };
        }

        return {
          data: [],
          total: 0,
          success: false,
        };
      }}
      rowKey="paymentId"
      cardBordered
      pagination={{
        defaultCurrent: 1,
        pageSize: pageSize,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50],
        showTotal: (total, range) =>
          lang.get("payment", [
            `Showing ${range[0]}-${range[1]} of ${total} items`,
          ]),
        onShowSizeChange: (current, size) => {
          setPageSize(size);
        },
      }}
      search={false}
      scroll={{ x: 800 }}
      toolBarRender={() => [
        <Space key="tools" size="middle" wrap>
          <RangePicker
            key="date"
            onChange={(value) => {
              const dateRange = value || [];
              const [startDate, endDate] = dateRange;

              if (startDate && endDate) {
                setFilters((prev) => [
                  ...prev.filter((f) => f.key !== "date"),
                  {
                    key: "date",
                    value: [
                      startDate.format("YYYY-MM-DD").toString(),
                      endDate.format("YYYY-MM-DD").toString(),
                    ],
                    type: "date_range",
                  },
                ]);
                actionRef?.current?.reload();
              }
            }}
          />
          <Input
            style={{ width: "auto" }}
            key="all_search"
            allowClear
            placeholder={lang.get("payment", ["Global Search"])}
            value={allSearch}
            onChange={(e) => {
              setAllSearch(e.target.value);
              if (actionRef.current) actionRef.current.reload();
            }}
          />
          <Dropdown.Button
            key="export"
            menu={{ items: exportItems }}
            type="primary"
          >
            Export
          </Dropdown.Button>
        </Space>,
      ]}
    />
  );
};

export default PaymentsTable;
